
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useRef, useState } from "react";
import { ModalComponent, ButtonComponent, CancelButton } from '@comps/components';
import { Icon } from '@iconify/react';
import { Col, Row } from "antd";
import moment from "moment";
import TeethIcon from "./TeethIcon"
import QRCode from "react-qr-code";
import { fileUrl } from "@utils/axios";
import { safeHtml } from "@utils/safeHtml";
import { parseJsonList } from "@utils/helpers";

const SafePdfComponent = ({ onClosed, caseData, treatmentplan }) => {
  const [loader, setLoader] = useState(false);
  const headerRef = useRef();
  const stepRefs = useRef([]);
  const footerRef = useRef();

  const waitForImages = async (element) => {
    const images = element.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (!img.complete) {
          return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          });
        }
      })
    );
  };

  const handleDownloadPdf = async () => {
    setLoader(true);

    // html2canvas blocks the main thread outright, so React would commit
    // the disabled button and the browser would never get a frame to
    // paint it in — the button stayed live-looking for the whole capture
    // and invited a second click. Two frames guarantees one paint first.
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      // Capture each section (header, each treatment step, footer)
      // separately instead of one giant screenshot — that way pagination
      // can decide, per section, whether it fits on the current page or
      // needs a fresh one, instead of blindly slicing at a fixed height
      // and cutting straight through the middle of a diagram.
      const sections = [headerRef.current, ...stepRefs.current, footerRef.current].filter(Boolean);

      // Gap drawn between two sections stacked on the same page (mm) —
      // each section image is captured flush to its own content, so
      // without this, sections butt up against each other in the PDF
      // with zero space regardless of any CSS margin between them
      // on-screen (that margin only affects spacing *inside* a section,
      // not the gap between two separately-captured section images).
      const SECTION_GAP_MM = 8;

      // Sections were drawn at x=0 across the full sheet width, so text
      // and diagrams ran into the paper's edge and the first one started
      // flush against the top. A4 is 210mm wide; 12mm each side is a
      // normal document margin and still leaves 186mm of content.
      const MARGIN_MM = 12;

      // A treatment plan is a page of text and one line-art diagram per
      // step, and it was leaving here at ~100MB: every section was
      // captured at scale 3 and embedded as a lossless PNG, so a plan
      // with twenty steps carried twenty full-width 24-megapixel
      // screenshots. Nothing downstream needs that — it is printed or
      // read on screen.
      //
      // scale 2 across an A4 content width is ~180 DPI, past what print
      // resolves, and JPEG at 0.92 is indistinguishable on this content
      // while costing a fraction of PNG on the photo and the anti-
      // aliased diagrams alike. jsPDF's own deflate pass then squeezes
      // the object streams. Together this lands in the low single-digit
      // megabytes.
      const CAPTURE_SCALE = 2;
      const JPEG_QUALITY = 0.92;

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - MARGIN_MM * 2;
      const contentHeight = pageHeight - MARGIN_MM * 2;
      let currentY = 0;
      let isFirstSection = true;

      for (const section of sections) {
        await waitForImages(section);

        const canvas = await html2canvas(section, {
          scale: CAPTURE_SCALE,
          useCORS: true,
          logging: false,
          // html2canvas renders onto a transparent canvas; a JPEG has no
          // alpha, so without this the transparent ground comes out
          // black instead of white.
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        // No gap needed at the very top of a page — only between two
        // sections sharing the same page. Recomputed after a possible
        // page break below, since currentY resets to 0 in that case.
        let y = currentY === 0 ? 0 : currentY + SECTION_GAP_MM;

        // Start a fresh page if this section won't fit in what's left of
        // the current one — never split a section across the boundary.
        if (!isFirstSection && y + imgHeight > contentHeight) {
          pdf.addPage();
          currentY = 0;
          y = 0;
        }
        isFirstSection = false;

        if (imgHeight > contentHeight) {
          // Rare: a single section (e.g. an oversized diagram) is taller
          // than one whole page on its own. currentY is guaranteed 0 here
          // (either this is the first section, or the check above just
          // moved us to a fresh page) — slice just this section across
          // as many pages as it needs.
          let heightLeft = imgHeight;
          let position = 0;
          pdf.addImage(imgData, "JPEG", MARGIN_MM, MARGIN_MM + position, contentWidth, imgHeight);
          heightLeft -= contentHeight;
          while (heightLeft > 0) {
            position -= contentHeight;
            pdf.addPage();
            pdf.addImage(imgData, "JPEG", MARGIN_MM, MARGIN_MM + position, contentWidth, imgHeight);
            heightLeft -= contentHeight;
          }
          currentY = 0;
          continue;
        }

        pdf.addImage(imgData, "JPEG", MARGIN_MM, MARGIN_MM + y, contentWidth, imgHeight);
        currentY = y + imgHeight;
      }

      pdf.save("treatement-plan-" + caseData.case_number + ".pdf");
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error("Error generating PDF:", error);
    }
  };

  const footer = (
    <div className="form-actions">
      <CancelButton key="close" onClick={ () => onClosed(false) } text="Close" />
      <ButtonComponent
        key="download"
        type="primary"
        state={ loader }
        icon={ <Icon icon="tabler:download" width={ 18 } /> }
        onClick={ handleDownloadPdf }
      >
        Download PDF
      </ButtonComponent>
    </div>
  )

  const convertImageUrlToBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Important for CORS!
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        try {
          const dataURL = canvas.toDataURL("image/png"); // or "image/jpeg"
          resolve(dataURL);
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = (err) => {
        reject(new Error("Image load failed: " + err));
      };
    });
  };



  const mainTitle = caseData?.name ? `${ caseData.name } Treatment Plan` : "Treatment plan";

  return <ModalComponent style={ { top: 10 } } width="60%" icon="tabler:file-type-pdf" mainTitle={ mainTitle } subTitle="Review it, then download a copy" open={ true } footer={ footer } onCancel={ () => onClosed(false) }>
    <div style={ {
      height: '500px',
      overflow: 'auto',
    } }>
      <div style={ { padding: 20, paddingRight: 30, paddingLeft: 30 } }>
        <div ref={ headerRef }>
          <div style={ { borderBottom: '1px solid lightgrey', paddingBottom: 12, marginBottom: 20 } }>
            <h3>Patient case card ID:  { caseData.case_number }</h3>
          </div>
          {/* Plain flexbox with a fixed-width photo column instead of
              antd's Row/Col breakpoints — Col's lg/sm sizing is based on
              the *window's* width, not this modal's actual (narrower)
              width, so the photo could end up wider than its column and
              overlap the text next to it. */}
          <div style={ { display: 'flex', alignItems: 'flex-start', gap: 24, marginBottom: 28 } }>
            <div style={ { flex: '0 0 150px' } }>
              { caseData.photo && <img style={ { width: 150, borderRadius: '15px' } } crossOrigin="anonymous" src={ fileUrl(caseData.photo) } alt="photo" /> }
            </div>
            <div style={ { flex: 1 } }>
              <p style={ { marginBottom: 8 } }>Patient information</p>
              <p>Name : <strong>{ caseData.name }</strong></p>
              <p>Gender : <strong>{ caseData.gender }</strong></p>
              <p>Date of Birth : <strong>{ moment(caseData.dob).format('DD MMM YYYY') }</strong></p>
            </div>
            <div style={ { flex: 1 } }>
              <p style={ { marginBottom: 8 } }>Case information</p>
              <p>Case ID : <strong>{ caseData.case_number }</strong></p>
              <p>Creation Date : <strong>{ moment(caseData.created_at).format('DD MMM YYYY hh:mm') + " (" + moment(caseData.created_at).format('HH:mm A') + ")" }</strong></p>
            </div>
          </div>

          <div style={ { marginBottom: 20 } }>
            <h4 style={ { marginBottom: 10 } }>Type of malocclusion and method of treatment:</h4>
            <h4 style={ { marginBottom: 14 } }>Required number of aligners:</h4>
            <Row>
              <Col span={ 4 }></Col>
              <Col span={ 10 }>Aligners to the upper jaw: { treatmentplan && treatmentplan.aligner_upper_jaw }</Col>
              <Col span={ 10 }>Aligners to the lower jaw: { treatmentplan && treatmentplan.aligner_lower_jaw }</Col>
              <Col span={ 4 }></Col>
              <Col span={ 10 } style={ { marginTop: 8 } }>Templates for attachments to the upper jaw: { treatmentplan && treatmentplan.attachment_upper_jaw }</Col>
              <Col span={ 10 } style={ { marginTop: 8 } }>Templates for attachments to the lower jaw: { treatmentplan && treatmentplan.attachment_lower_jaw }</Col>
            </Row>
          </div>

          <div style={ { marginBottom: 20 } }>
            <h4 style={ { marginBottom: 8 } }>Orthodontist notes:</h4>
            <div style={ { paddingLeft: 200 } }
              dangerouslySetInnerHTML={ safeHtml(treatmentplan && treatmentplan.orthodontist_notes) } />
          </div>
          <div style={ { marginBottom: 28 } }>
            <h4 style={ { marginBottom: 8 } }>Technician's notes:</h4>
            <div style={ { paddingLeft: 200 } }
              dangerouslySetInnerHTML={ safeHtml(treatmentplan && treatmentplan.technician_notes) } />
          </div>
        </div>
        { treatmentplan && treatmentplan.iprsattachment && parseJsonList(treatmentplan.iprsattachment.treatment_steps).map((each, index) =>
          <div key={ index } ref={ (el) => (stepRefs.current[index] = el) } style={ { marginBottom: 30, paddingTop: 10 } }>
            <h4 style={ { marginBottom: 14 } }>Treatment step: { index + 1 }</h4>
            <div style={ { width: '100%', display: 'flex', justifyContent: 'center' } }>
              <div >
                <TeethIcon disableAction={ true } selectedAttachments={ parseJsonList(treatmentplan.iprsattachment.attachments) } stepIndex={ index } iprs={ parseJsonList(treatmentplan.iprsattachment.iprs) } />
              </div>
            </div>
            <p style={ { textAlign: 'center', marginTop: 14 } }>From { each.from } to { each.to } aligner</p>
          </div>
        ) }
        <div ref={ footerRef } style={ { paddingTop: 10 } }>
          <h4 style={ { marginBottom: 16 } }>Card URL for the doctor:</h4>
          <div style={ { height: "auto", margin: "0 auto", maxWidth: 256, width: "100%" } }>
            { treatmentplan && treatmentplan['3d_Setup_link'] ? (
              <QRCode
                size={ 256 }
                style={ { height: "auto", maxWidth: "100%", width: "100%" } }
                value={ treatmentplan['3d_Setup_link'] }
                viewBox={ `0 0 256 256` }
              />
            ) : (
              <p style={ { textAlign: 'center', color: '#888' } }>No 3D setup link available</p>
            ) }
          </div>
        </div>
      </div>
    </div>
  </ModalComponent>;
}

export default SafePdfComponent;
